import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Getting started → Set up preferences.
// Fixture: `reviewTheDraft` (the demo officer with preferences already set).
// The scene opens from the case list, clicks the sliders icon, walks the
// Report Preferences page, and re-selects the current values so nothing
// visibly changes before Save Preferences.
const field = (key: string) => `${sel("preferenceField")}[data-pref="${key}"]`;
const trigger = (key: string) => `${field(key)} .dropdown > button`;
const current = (key: string) => `${field(key)} .dropdown-content li p.bg-base-300`;

const scene: Scene = {
  id: "set-up-your-report-preferences",
  title: "Set up your report preferences",
  startRoute: "/cases",
  fixture: "reviewTheDraft",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "Every draft is written against your Report Preferences, so set them once before you upload anything. Click the sliders icon next to New Case.",
      actions: [
        // /cases opens the first report on its own once the list loads. Wait
        // for that, or a fast-forwarded seek clicks before it and the app
        // navigates back to the report right after the preferences page opens.
        { type: "waitFor", target: sel("reportScore") },
        { type: "waitFor", target: sel("preferencesButton") },
        { type: "wait", ms: 5200 },
        { type: "spotlight", target: sel("preferencesButton") },
        { type: "wait", ms: 1000 },
        { type: "click", target: sel("preferencesButton") },
        { type: "spotlight", target: null },
        { type: "waitFor", target: sel("preferencesForm") },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "identity",
      narration:
        "At the top are the fields the AI uses to refer to you: Include Name, Include Rank or Title, and Include Badge Number. Each has a toggle, and rank has a value you type.",
      actions: [
        { type: "wait", ms: 2400 },
        { type: "spotlight", target: field("name") },
        { type: "wait", ms: 1500 },
        { type: "spotlight", target: field("rank") },
        { type: "wait", ms: 1800 },
        { type: "scrollIntoView", target: field("badge_number") },
        { type: "spotlight", target: field("badge_number") },
        { type: "wait", ms: 3200 },
        { type: "spotlight", target: field("rank") },
        { type: "wait", ms: 1800 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "rank-style",
      narration:
        "If your department writes OFC or SGT, turn on Abbreviate Rank or Capitalize Rank.",
      actions: [
        { type: "wait", ms: 2700 },
        { type: "spotlight", target: field("abbreviate_rank") },
        { type: "wait", ms: 1800 },
        { type: "spotlight", target: field("capitalize_rank") },
        { type: "wait", ms: 1500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "perspective",
      narration:
        "Narrative Perspective is a big one. First person says I, third person says Officer Smith. Subject Reference Style controls how victims, suspects, and witnesses are named after the first mention.",
      actions: [
        { type: "scrollIntoView", target: field("perspective") },
        { type: "wait", ms: 500 },
        { type: "click", target: trigger("perspective") },
        { type: "waitFor", target: current("perspective") },
        { type: "wait", ms: 4600 },
        { type: "click", target: current("perspective") },
        { type: "wait", ms: 300 },
        { type: "spotlight", target: field("subject_reference_style") },
        { type: "wait", ms: 4600 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "formats",
      narration:
        "Set Date Format and Time Format to match your department's reports, military time or twelve hour, whichever your reports use.",
      actions: [
        { type: "scrollIntoView", target: field("date_format") },
        { type: "spotlight", target: field("date_format") },
        { type: "wait", ms: 1500 },
        { type: "spotlight", target: field("time_format") },
        { type: "wait", ms: 700 },
        { type: "click", target: trigger("time_format") },
        { type: "waitFor", target: current("time_format") },
        { type: "wait", ms: 2000 },
        { type: "click", target: current("time_format") },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "extras",
      narration:
        "Turn on Include Topic Headings if you want section headings in the draft, and set Include Statutes to your state so the Statutes panel works.",
      actions: [
        { type: "scrollIntoView", target: field("topic_headings") },
        { type: "spotlight", target: field("topic_headings") },
        { type: "wait", ms: 3800 },
        { type: "scrollIntoView", target: field("statute_state") },
        { type: "spotlight", target: field("statute_state") },
        { type: "wait", ms: 3500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "save",
      narration: "When you're done, click Save Preferences.",
      actions: [
        { type: "moveTo", target: sel("preferencesSave") },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("preferencesSave") },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "outro",
      narration:
        "Perspective, time format, date format, and rank are the ones officers change most after seeing a first draft, so start there. Changing a preference doesn't rewrite old reports. It applies to new drafts and to any report you regenerate.",
      actions: [{ type: "wait", ms: 4000 }],
    },
  ],
};

export default scene;
